// #v-ifdef IS_PROD='true'
console.error('Cheats tryed to enable on prod');
import type { BladeState, FolderParams } from '@tweakpane/core';
import type { FolderApi } from 'tweakpane';
import type { Application } from 'pixi.js';
import type {
    DebugFields,
    DevToolsConfig,
    FieldsAPI,
    Position,
    SectionConfig,
    SelectorsAPI,
} from './DevTools.types';
// #v-else
import ls from 'localstorage-slim';
import { Pane } from 'tweakpane';

import { initMobileConsole, removeMobileConsole } from './tools/MobileConsole';
import { PixiStats } from './tools/PixiStats';

export class DevTools extends Pane {
    protected container: HTMLDivElement;
    #pixi?: Application;

    pixiStats!: PixiStats | null;
    debugFolder!: FolderApi;
    settingsFolder!: FolderApi;

    constructor(private config: DevToolsConfig) {
        const container = document.createElement('div');

        container.style.maxHeight = '100%';
        container.style.overflowY = 'auto';
        container.style.zIndex = '1000';

        super({
            title: 'DevTools',
            expanded: false,
            container,
        });

        this.container = container;
        this.setPosition(config?.position);

        this.init(this.config?.pixi);
    }

    private async init(pixi?: Application) {
        this.debugFolder = this.addFolder({
            title: 'Debug',
            expanded: false,
        });

        this.settingsFolder = this.addFolder({
            title: 'Settings',
            expanded: false,
        });

        // console.log(`💀 ${APP_NAME} ${APP_VERSION} ${APP_MODE}`);

        this.element.querySelector('.tp-rotv_b')?.addEventListener('click', () => this.saveState());

        this.addMobileConsole();

        if (pixi) {
            this.pixi = pixi;
        }

        this.loadState();

        this.on('change', () => this.saveState());
        document.body?.appendChild(this.container);
    }

    set pixi(app: Application) {
        window.__PIXI_APP__ = app;
        this.#pixi = app;
        this.addPixiStats();
    }

    get pixi(): Application | undefined {
        return this.#pixi;
    }

    setPosition(position?: Position) {
        this.container.style.position = 'fixed';
        this.container.style.top = '3px';
        this.container.style.left = '3px';

        switch (position?.x) {
            case 'left':
                this.container.style.left = '0';
                this.container.style.right = '';
                break;
            case 'right':
                this.container.style.right = '0';
                this.container.style.left = '';
                break;
        }

        switch (position?.y) {
            case 'top':
                this.container.style.top = '0';
                this.container.style.bottom = '';
                break;
            case 'bottom':
                this.container.style.bottom = '0';
                this.container.style.top = '';
                break;
        }
    }

    protected loadState() {
        if (!ls.get('DevTools')) return;

        const state = ls.get('DevTools') as BladeState;

        this.importState(state);

        this.refresh();
    }

    protected saveState() {
        const state = this.exportState();

        ls.set('DevTools', state);
    }

    protected addPixiStats() {
        this.debugFolder
            .addBinding({ PixiStats: ls.get('PixiStats') === true }, 'PixiStats')
            .on('change', ({ value }) => {
                this.updatePixiStats(value);
            });

        if (ls.get('PixiStats') === true) {
            this.updatePixiStats(true);
        }
    }

    protected updatePixiStats(enabled: boolean) {
        ls.set('PixiStats', enabled);

        if (enabled && !this.pixiStats && this.#pixi) {
            this.pixiStats = new PixiStats(this.#pixi);
        }

        if (!enabled && this.pixiStats) {
            this.pixiStats.remove();
            this.pixiStats = null;
        }
    }

    protected addMobileConsole() {
        this.debugFolder
            .addBinding({ MobileConsole: false }, 'MobileConsole')
            .on('change', ({ value }) => {
                if (value) {
                    initMobileConsole();
                } else {
                    removeMobileConsole();
                }
            });
    }

    override addFolder(params: FolderParams): FolderApi {
        const folder = super.addFolder(params);

        folder.element.addEventListener('click', () => this.saveState());

        this.loadState();

        return folder;
    }

    addSection(config: SectionConfig): {
        section: FolderApi;
        fields: FieldsAPI;
        selectors: SelectorsAPI;
    } {
        const section = this.addFolder({
            title: config.title,
            expanded: config.expanded,
        });

        const fields: FieldsAPI = {};
        const selectors: SelectorsAPI = {};

        for (const fieldID in config.fields) {
            const field = config.fields[fieldID];

            switch (field.type) {
                case 'select':
                    // eslint-disable-next-line no-case-declarations
                    const options: {
                        options: Record<string, string>;
                    } = {
                        options: {
                            [field.title]: '',
                        },
                    };

                    for (const option in field.options) {
                        options.options[option] = field.options[option];
                    }

                    // eslint-disable-next-line no-case-declarations
                    const select = { [fieldID]: '' };

                    selectors[fieldID] = {
                        set: (val: string) => {
                            select[fieldID] = val;

                            this.refresh();
                            this.saveState();
                        },
                    };

                    fields[fieldID] = section
                        .addBinding(select, fieldID, options)
                        .on('change', ({ value }) => {
                            field.cb?.(value);
                        });
                    break;
                case 'button':
                    fields[fieldID] = section.addButton({ title: field.title }).on('click', () => {
                        field.cb?.('');
                    });
                    break;
                case 'checkBox':
                    this.addCheckBox({
                        fieldID,
                        field,
                        fields,
                        section,
                    });
                    break;
            }
        }

        return {
            section,
            fields,
            selectors,
        };
    }

    private addCheckBox({
        fieldID,
        field,
        fields,
        section,
    }: {
        fieldID: string;
        field: DebugFields[string];
        fields: FieldsAPI;
        section: FolderApi;
    }) {
        const value = { [fieldID]: field.val ?? false };

        fields[fieldID] = section
            .addBinding(value, fieldID)
            .on('change', ({ value }) => field.cb?.(value));
    }
}
// #v-endif
